import { useCallback, useEffect, useContext, useState } from 'react';
import Error from '../../components/ErrorBoundary';
import NotFound from '../404';
import { Link, useSearchParams } from 'react-router';
import ProductCard from '../../components/ProductCard';
import ProductCtaCard from '../../components/ProductCtaCard';
import API from '../../services/apiService';
import ViewCartBtn from '../../components/ViewCartBtn';
import '../../scss/product-cta-card.scss';
import { CatalogDispatchContext } from '../../store/CatalogContext';
import parse from 'html-react-parser';
import cookies from '../../utils/cookies';

import type { Product, UpsellItem } from '../../types/product';
import ProductPageSkeleton from '../../components/loading-skeletons/ProductPageSkeleton';

const ProductDescription = () => {
  const [searchParams] = useSearchParams();
  const { id, tp, cc, gid } = Object.fromEntries([...searchParams]);
  const catalogDispatch = useContext(CatalogDispatchContext); // may be unused after comments

  // console.log('ProductDescription id', id);

  const [productNotFound, setProductNotFound] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedUpsells, setRelatedUpsells] = useState<UpsellItem[] | [] | null>(null);

  useEffect(() => {
    document.documentElement.classList.add('bg-white');

    return () => {
      // Clean up the script when component unmounts
      document.documentElement.classList.remove('bg-white');
    };
  }, []);

  useEffect(() => {
    if (product?.upsellProducts) {
      setRelatedUpsells(
        product.upsellProducts
          .filter((item) => item.upsellSectionId === 5)
          .sort((a, b) => a?.upsellOrderIndex - b.upsellOrderIndex)
      );
    }
  }, [product]);

  const getProduct = useCallback(
    (id: string, controller: AbortController) => {
      const data = {
        productIds: [id],
        testPrice: tp,
        groupId: gid,
        coupon: cc,
        // fullSiteBuild: true, // default is true
        includeUpsells: true,
      };

      API.getProductsByIds(data, controller.signal)
        .then((res) => {
          const product = res.data[0] as Product;

          if (!product) {
            setProductNotFound(true);

            // catalogDispatch({
            //   type: 'ERROR',
            //   payload: {
            //     isLoading: false,
            //     isLoadingProductDetails: false,
            //     error: 'Product not found',
            //   },
            // });
            return;
          }

          setProduct(product);

          // setting test price cookie if it is applied to any product
          const isTestPriceApplied = product?.pricing?.typePricing?.toLowerCase() === tp?.toLowerCase();
          if (tp && isTestPriceApplied) {
            cookies.set('_up4tp', tp.toLowerCase(), 30);
          }

          // if queryGroupId or defaultGroupId is used on any product, set it as a cookie
          const isGroupIdApplied = product.pricing.groupId?.toLowerCase() === gid?.toLowerCase();
          if (gid && isGroupIdApplied) {
            cookies.set('_up4gid', gid.toLowerCase(), 30);
          }

          const isCouponApplied = product.pricing.discountSource === 'Coupon';
          if (cc && isCouponApplied) {
            cookies.set('_up4cc', cc.toLowerCase(), 30);
          }
        })
        .catch((err) => {
          if (err?.name === 'CanceledError') {
            console.error('Request Aborted');
          } else if (err?.name === 'AxiosError') {
            // console.error(err);
            console.error(err?.response?.data);
          } else {
            console.error(err);
          }

          // show error message or reload the page
          if (err?.name !== 'CanceledError') {
            setProductNotFound(true);

            // catalogDispatch({
            //   type: 'ERROR',
            //   payload: {
            //     isLoading: false,
            //     isLoadingProductDetails: false,
            //     error: err?.response?.data || 'General Error',
            //   },
            // });
          }
        });
    },
    [catalogDispatch]
  );

  // fetch product details if there is an id
  useEffect(() => {
    const controller = new AbortController();

    // fetch product details if there is an id
    if (id) {
      getProduct(id, controller);
    }

    return () => controller.abort();
  }, [id, tp, cc, gid, getProduct]);

  // if product id is not provided, show 404 page
  // this is to handle cases where the slug is provided but the product id is missing
  if (!id || productNotFound) {
    return <NotFound />;
  }

  if (!product) {
    return <ProductPageSkeleton />;
  }

  return (
    <>
      <title>{product?.name}</title>
      {/* <link rel="canonical" href={`https://www.${domain}`} /> */}

      <div className="wrap product-header border-bottom py-3 py-lg-4 mb-3">
        <div className="container-fluid but-fixed">
          <div className="row justify-content-between">
            <header className="col-9 col-lg-10">
              <h1 className="mb-0">{product?.name}</h1>
            </header>
            <div className="col-3 col-lg-2 d-flex justify-content-end align-items-center">
              <ViewCartBtn />
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid but-fixed py-0 mb-lg-4">
        <div className="row justify-content-center">
          <div className="col-12 mb-1 mb-md-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb text-muted mb-0">
                <li className="breadcrumb-item">
                  <Link className="text-reset" to="/">
                    &raquo; Home
                  </Link>
                </li>
                {/* <li className="breadcrumb-item">
                  <Link className="text-reset" to="#">
                    Category
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link className="text-reset" to="#">
                    Sub-Category
                  </Link>
                </li> */}
                <li className="breadcrumb-item active d-none d-md-inline-block" aria-current="page">
                  {product?.name}
                </li>
              </ol>
            </nav>
          </div>

          <div className="col-12 col-sm-10 col-md-8 col-lg-5 col-xl-4 order-lg-1 my-2 mb-sm-3">
            <ProductCtaCard product={product!} />
          </div>

          <div className="col-12 col-lg-7 col-xl-8 my-3 order-lg-0">
            {parse(product?.longDescription ? product?.longDescription : product?.description ? product?.description : '')}
          </div>
        </div>

        {relatedUpsells && relatedUpsells.length > 0 && (
          <div className="others-also-purchased row my-3 my-lg-4">
            <div className="col-12">
              <h3 className="fs-4 text-theme-success mb-3">Others Also Purchased</h3>
              <div className="row product-list-display grid-col-3 gx-3 gx-xl-4">
                {relatedUpsells.map((item) => (
                  <ProductCard key={item.id} product={item} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProductDescription;

export const ErrorBoundary = () => {
  return <Error fromPage="Product Description Page" />;
};
